import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { API, monitorApi } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { PlaneTakeoff as _PlaneTakeoff } from "lucide-react";
import { flightDoctorState } from "@/lib/flightDoctor";
import { PRIORITIES } from "@/lib/constants";
import { makeBaseLayer, loadMapConfig } from "@/lib/mapBase";
import { Tv, MapPin, Truck, Power, Radio, KeyRound, LogOut, Plus, CheckCheck, Settings2, Building2, Fuel, StickyNote, Stethoscope } from "lucide-react";

function HelicopterIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 4h18" /><path d="M12 4v3.5" />
      <path d="M4.5 9.5h8.5a4.5 4.5 0 0 1 4.5 4.5v1H9a4.5 4.5 0 0 1-4.5-4.5v-1z" />
      <path d="M9 15.5V19" /><path d="M6 19h7" /><path d="M17.5 13l4-1.2" />
    </svg>
  );
}

function SpeechSettings({ rate, setRate, volume, setVolume, speak }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="monitor-speech-btn" onClick={() => setOpen(true)} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-100"><Settings2 className="mr-1 inline h-3.5 w-3.5" />Spraak</button>
      {open && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div data-testid="monitor-speech-modal" className="w-full max-w-sm rounded-2xl bg-white p-5 text-left text-slate-900" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-base font-bold">Spraakinstellingen</div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Spreeksnelheid: {rate.toFixed(2)}×</label>
            <input data-testid="monitor-speech-rate" type="range" min="0.5" max="1.5" step="0.02" value={rate} onChange={(e) => setRate(parseFloat(e.target.value))} className="w-full accent-blue-600" />
            <label className="mb-1 mt-3 block text-xs font-semibold text-slate-500">Volume: {Math.round(volume * 100)}%</label>
            <input data-testid="monitor-speech-volume" type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="w-full accent-blue-600" />
            <div className="mt-4 flex gap-2">
              <button data-testid="monitor-speech-test" onClick={() => speak("Prioriteit 1. Testmelding. Locatie kazerne.")} className="flex-1 rounded-lg bg-slate-100 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">Test</button>
              <button onClick={() => setOpen(false)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">Sluiten</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const pin = (color = "#dc2626") => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:${color};border:3px solid #fff;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>`,
  iconAnchor: [13, 24],
});

function MonitorMap({ inc, shared, legend }) {
  const el = useRef(null);
  const lat = inc.building ? inc.building.lat : inc.location_lat;
  const lng = inc.building ? inc.building.lng : inc.location_lng;
  useEffect(() => {
    if (!el.current || lat == null || lng == null) return;
    let cancelled = false;
    const map = L.map(el.current, { zoomControl: false, attributionControl: false }).setView([lat, lng], 16);
    L.marker([lat, lng], { icon: pin() }).addTo(map);
    const addGeo = (gj, dflt, dashed) => {
      if (!gj || !Array.isArray(gj.features) || !gj.features.length) return;
      L.geoJSON(gj, {
        pointToLayer: (f, ll) => L.marker(ll, { icon: pin((f.properties && f.properties.color) || dflt) }),
        style: (f) => { const col = (f.properties && f.properties.color) || dflt; const poly = f.geometry.type.indexOf("Polygon") >= 0; return poly ? { color: col, weight: 2, fillColor: col, fillOpacity: 0.12, dashArray: dashed ? "4 4" : null } : { color: col, weight: dashed ? 3 : 4, opacity: 0.85, dashArray: dashed ? "4 4" : null }; },
        onEachFeature: (f, layer) => { const t = f.properties && f.properties.label; if (t) layer.bindTooltip(String(t), { permanent: true, direction: "top" }); },
      }).addTo(map);
    };
    addGeo(inc.map_drawings, "#2563eb", false);
    addGeo(shared, "#0d9488", true);
    loadMapConfig().then((cfg) => {
      if (cancelled || !map._container) return;
      const b = makeBaseLayer(cfg || {}, false).addTo(map); b.bringToBack();
    }).catch(() => {});
    const t = setTimeout(() => { if (!cancelled && map._container) map.invalidateSize(); }, 200);
    return () => { cancelled = true; clearTimeout(t); try { map.remove(); } catch (_) {} };
  }, [inc.id, lat, lng, shared]);
  const usedColors = new Set();
  if (inc.map_drawings && Array.isArray(inc.map_drawings.features)) inc.map_drawings.features.forEach((f) => usedColors.add((f.properties && f.properties.color) || "#2563eb"));
  if (shared && Array.isArray(shared.features)) shared.features.forEach((f) => usedColors.add((f.properties && f.properties.color) || "#0d9488"));
  const legendEntries = legend ? Object.entries(legend).filter(([c, v]) => v && usedColors.has(c)) : [];
  if (lat == null || lng == null) return <div className="flex h-full items-center justify-center text-slate-500">Geen locatie beschikbaar</div>;
  return (
    <div className="relative h-full w-full">
      <div ref={el} className="h-full w-full" data-testid="monitor-map" />
      {legendEntries.length > 0 && (
        <div data-testid="monitor-legend" className="absolute bottom-3 left-3 z-[1001] rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Legenda</div>
          <div className="space-y-1">
            {legendEntries.map(([c, v]) => (
              <div key={c} className="flex items-center gap-2">
                <span className="h-3 w-3 shrink-0 rounded-full border border-white shadow" style={{ backgroundColor: c }} />
                <span className="text-xs font-medium text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

const locOf = (inc) => inc.building?.name || inc.location_label || "Onbekende locatie";
const vehOf = (inc) => (inc.vehicles || []).map((v) => v.callsign).join(", ");

function fmtCountdown(dateStr, tstr, now) {
  if (!tstr) return null;
  const [h, m] = tstr.split(":").map(Number);
  const target = new Date(`${dateStr}T00:00:00`); target.setHours(h, m, 0, 0);
  const diff = target - now;
  if (diff <= -3600000) return null;
  if (diff <= 0) return "Nu bezig";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `Over ${mins} min.`;
  const H = Math.floor(mins / 60), M = mins % 60;
  return `Over ${H}u${M ? " " + M + "m" : ""}`;
}

function minsTo(dateStr, tstr, now) {
  if (!tstr) return null;
  const [h, m] = tstr.split(":").map(Number);
  const target = new Date(`${dateStr}T00:00:00`); target.setHours(h, m, 0, 0);
  return Math.round((target - now) / 60000);
}

function MonitorTaskCard({ task, now, dateStr: _ds, onToggle, isNext }) {
  const dateStr = task.date || new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const cd = task.done ? null : fmtCountdown(dateStr, task.start_time, now);
  const timed = !!task.start_time;
  const mins = timed && !task.done ? minsTo(dateStr, task.start_time, now) : null;
  const within15 = mins != null && mins > 0 && mins <= 15;
  const highlight = isNext && !task.done && !within15;
  return (
    <div data-testid={`monitor-task-${task.id}`}
      className={`flex items-center gap-4 gold-widget rounded-2xl px-5 py-4 transition-colors ${task.done ? "opacity-60" : within15 ? "border-yellow-400 bg-yellow-50 ring-2 ring-yellow-400 animate-pulse" : highlight ? "border-yellow-400 bg-white ring-2 ring-yellow-400" : "border-slate-200 bg-white"}`}>
      {!timed && (
        <button data-testid={`monitor-task-toggle-${task.id}`} onClick={() => onToggle(task)}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 text-lg ${task.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white text-transparent hover:border-slate-400"}`}>✓</button>
      )}
      <div className="min-w-0 flex-1 text-left">
        <div className={`flex items-center text-2xl font-bold ${task.done ? "text-slate-400 line-through" : "text-slate-900"}`}>
          <span className="min-w-0 line-clamp-2 break-words">{task.title}</span>
        </div>
        {timed && (
          <div className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-500">
            <span>{task.start_time}{task.end_time ? ` – ${task.end_time}` : ""}</span>
            {(highlight || within15) && <span data-testid={`monitor-task-next-${task.id}`} className="whitespace-nowrap rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-bold text-slate-900">Nu aankomend</span>}
          </div>
        )}
      </div>
      {cd && <div className={`shrink-0 rounded-full px-4 py-1.5 text-lg font-bold ${within15 ? "bg-yellow-400 text-slate-900" : "bg-slate-900 text-white"}`}>{cd}</div>}
      {task.done && <div className="shrink-0 text-sm font-semibold text-emerald-600">Afgerond</div>}
    </div>
  );
}

function MonitorTaskBoard({ tasks, now, dateStr, timeStr, onToggle }) {
  const timedTasks = tasks.filter((t) => t.start_time).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const otherTasks = tasks.filter((t) => !t.start_time);
  const isoToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const nextTask = timedTasks.find((t) => !t.done && minsTo(t.date || isoToday, t.start_time, now) > 0);
  const nextId = nextTask ? nextTask.id : null;
  return (
    <div className="mt-4 w-full max-w-6xl" data-testid="monitor-tasks">
      <div className="mb-4 flex flex-col items-center">
        <div data-testid="monitor-tasks-clock" className="font-clock font-extrabold leading-[0.85] text-slate-900" style={{ fontSize: "min(24rem, 38vh)" }}>{timeStr}</div>
        <div className="mt-1 text-lg capitalize text-slate-500">{dateStr}</div>
      </div>
      {tasks.length === 0 ? (
        <div className="gold-widget rounded-2xl py-10 text-center text-2xl font-semibold text-slate-500">Geen taken voor vandaag</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div data-testid="monitor-tasks-timed">
            <div className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-slate-500">Op tijd</div>
            <div className="space-y-3">
              {timedTasks.length === 0
                ? <div className="gold-widget rounded-2xl py-6 text-center text-lg text-slate-500">Geen tijdgebonden taken</div>
                : timedTasks.map((task) => <MonitorTaskCard key={task.id} task={task} now={now} dateStr={dateStr} onToggle={onToggle} isNext={task.id === nextId} />)}
            </div>
          </div>
          <div data-testid="monitor-tasks-other">
            <div className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-slate-500">Overige taken</div>
            <div className="space-y-3">
              {otherTasks.length === 0
                ? <div className="gold-widget rounded-2xl py-6 text-center text-lg text-slate-500">Geen overige taken</div>
                : otherTasks.map((task) => <MonitorTaskCard key={task.id} task={task} now={now} dateStr={dateStr} onToggle={onToggle} isNext={false} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function KazerneMonitor() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const now = useClock();
  const [token, setToken] = useState(localStorage.getItem("monitorToken"));
  const [phase, setPhase] = useState("loading"); // loading | enroll | ready
  const [armed, setArmed] = useState(false);
  const [online, setOnline] = useState(false);
  const [lastMsgAt, setLastMsgAt] = useState(0);
  const [wsStale, setWsStale] = useState(false);
  const wsStatus = lastMsgAt === 0 ? "connecting" : wsStale ? "degraded" : "live";
  const [incidents, setIncidents] = useState([]);
  const [index, setIndex] = useState(0);
  const [notes, setNotes] = useState([]);
  const [extraByInc, setExtraByInc] = useState({});
  const [monLoc, setMonLoc] = useState(null);
  const [monLocName, setMonLocName] = useState("");
  const [idleMode, setIdleMode] = useState("");
  const [globalIdle, setGlobalIdle] = useState("time");
  const [tasks, setTasks] = useState([]);
  const [flActive, setFlActive] = useState("");
  const [flLeft, setFlLeft] = useState("");
  const [flRight, setFlRight] = useState("");
  const [monShowFl, setMonShowFl] = useState(false);
  const [flDoc, setFlDoc] = useState({ present: false, until: null });
  const [monShared, setMonShared] = useState(null);
  const [monLegend, setMonLegend] = useState(null);
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const armedRef = useRef(false);
  const monLocRef = useRef(null);
  const pauseUntilRef = useRef(0);
  monLocRef.current = monLoc;
  const current = incidents[index] || null;
  const currentRef = useRef(null);
  currentRef.current = current;

  // Bootstrap: known device → straight in; otherwise ask for the pairing code.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const urlTok = params.get("token");
      if (urlTok) localStorage.setItem("monitorToken", urlTok);
      const tok = localStorage.getItem("monitorToken");
      if (tok) {
        try {
          const { data } = await monitorApi.get("/auth/me");
          if (cancelled) return;
          setMonLoc(data.location_id || null); setMonLocName(data.location_name || ""); setIdleMode(data.idle_mode || ""); setToken(tok); setPhase("ready"); return;
        } catch (e) { localStorage.removeItem("monitorToken"); }
      }
      // No valid token — recover silently via the known device id before showing the code screen.
      const mid = localStorage.getItem("monitorDeviceId");
      if (mid) {
        try {
          const { data } = await axios.get(`${API}/monitor/status`, { params: { monitor_id: mid } });
          if (cancelled) return;
          if (data.status === "approved" && data.token) {
            localStorage.setItem("monitorToken", data.token);
            setMonLoc(data.location_id || null); setMonLocName(data.location_name || ""); setToken(data.token); setPhase("ready"); return;
          }
          localStorage.removeItem("monitorDeviceId");
        } catch (e) { localStorage.removeItem("monitorDeviceId"); }
      }
      if (!cancelled) setPhase("enroll");
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enroll = async (e) => {
    if (e) e.preventDefault();
    if (!code.trim()) return;
    try {
      const existing = localStorage.getItem("monitorDeviceId") || undefined;
      const { data } = await axios.post(`${API}/monitor/enroll`, { code: code.trim(), device_id: existing });
      localStorage.setItem("monitorToken", data.token);
      localStorage.setItem("monitorDeviceId", data.monitor_id);
      setToken(data.token); setPhase("ready");
    } catch (err) { toast.error(err.response?.data?.detail || "Koppelen mislukt"); }
  };

  const unpair = () => {
    localStorage.removeItem("monitorToken"); localStorage.removeItem("monitorDeviceId");
    setToken(null); setArmed(false); setIncidents([]); setIndex(0); armedRef.current = false; setPhase("enroll");
  };

  const [speechRate, setSpeechRate] = useState(() => { const v = parseFloat(localStorage.getItem("monSpeechRate")); return v > 0 ? v : 0.96; });
  const [speechVolume, setSpeechVolume] = useState(() => { const v = parseFloat(localStorage.getItem("monSpeechVolume")); return v >= 0 && v <= 1 ? v : 1; });
  const speechRateRef = useRef(0.96); speechRateRef.current = speechRate;
  const speechVolumeRef = useRef(1); speechVolumeRef.current = speechVolume;
  const announcedRef = useRef(new Set());
  const [repeatSecs, setRepeatSecs] = useState(0);
  useEffect(() => { localStorage.setItem("monSpeechRate", String(speechRate)); }, [speechRate]);
  useEffect(() => { localStorage.setItem("monSpeechVolume", String(speechVolume)); }, [speechVolume]);

  const speak = useCallback((text) => {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "nl-NL";
      const voices = synth.getVoices();
      const nl = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("nl"));
      if (nl) u.voice = nl;
      u.rate = speechRateRef.current; u.pitch = 1; u.volume = speechVolumeRef.current;
      synth.speak(u);
    } catch (_) {}
  }, []);

  const announce = useCallback((inc) => {
    const P = PRIORITIES[inc.priority] || PRIORITIES[4];
    const veh = vehOf(inc);
    speak(`${P.short}. ${inc.title}. Locatie ${locOf(inc)}. ${veh ? `Voor ${veh}.` : ""}`);
  }, [speak]);

  const loadNotes = useCallback((iid) => {
    monitorApi.get(`/incidents/${iid}/notes`).then(({ data }) => setNotes(data || [])).catch(() => setNotes([]));
  }, []);

  const matchesLoc = useCallback((inc) => {
    const locs = (inc.vehicles || []).map((v) => v.location_id).filter(Boolean);
    return !monLocRef.current || locs.length === 0 || locs.includes(monLocRef.current);
  }, []);

  useRealtime((msg) => {
    if (msg && msg.type) { setOnline(true); setLastMsgAt(Date.now()); setWsStale(false); }
    { const myEnv = localStorage.getItem("monitorEnvId"); if (msg.payload && msg.payload.env_id && myEnv && msg.payload.env_id !== myEnv) return; }
    if (msg.type === "__ping") return;
    if (msg.type === "map_layer_updated") { setMonShared(msg.payload && msg.payload.enabled !== false ? msg.payload.drawings : null); return; }
    if (msg.type === "color_legend_updated") { setMonLegend((msg.payload && msg.payload.legend) || null); return; }
    if (msg.type === "tasks_updated") { monitorApi.get("/tasks").then(({ data }) => setTasks(data || [])).catch(() => {}); return; }
    if (msg.type === "monitor_idle_updated") { monitorApi.get("/auth/me").then(({ data }) => { setIdleMode(data.idle_mode || ""); setMonLoc(data.location_id || null); setMonLocName(data.location_name || ""); }).catch(() => {}); return; }
    if (msg.type === "monitor_idle_updated") { monitorApi.get("/auth/me").then(({ data }) => setIdleMode(data.idle_mode || "")).catch(() => {}); return; }
    if (msg.type === "map_config_updated") { monitorApi.get("/settings/map").then(({ data }) => { setGlobalIdle(data.monitor_idle_mode || "time"); setFlActive(data.flightroute_active || ""); setFlLeft(data.flightroute_left_name || ""); setFlRight(data.flightroute_right_name || ""); setMonShowFl(!!data.monitor_show_flightroute); setFlDoc({ present: !!data.flight_doctor_present, until: data.flight_doctor_until || null }); }).catch(() => {}); return; }
    if (msg.type === "__ws_open") {
      if (armedRef.current) {
        monitorApi.get("/incidents?status=open").then(({ data }) => {
          const list = (data || []).filter(matchesLoc);
          setIncidents(list);
          setIndex((idx) => (list.length ? Math.min(idx, list.length - 1) : 0));
        }).catch(() => {});
        if (currentRef.current) loadNotes(currentRef.current.id);
      }
      return;
    }
    if (msg.type === "incident_created" && msg.payload) {
      if (!matchesLoc(msg.payload)) return;
      setIncidents((prev) => {
        if (prev.some((i) => i.id === msg.payload.id)) return prev.map((i) => (i.id === msg.payload.id ? msg.payload : i));
        const next = [...prev, msg.payload];
        setIndex(next.length - 1);
        return next;
      });
    }
    if (msg.type === "incident_updated" && msg.payload) {
      const p = msg.payload;
      setIncidents((prev) => {
        if ((p.status && p.status !== "open") || !matchesLoc(p)) return prev.filter((i) => i.id !== p.id);
        if (prev.some((i) => i.id === p.id)) return prev.map((i) => (i.id === p.id ? p : i));
        const next = [...prev, p];
        setIndex((idx) => Math.min(idx, next.length - 1));
        return next;
      });
    }
    if (msg.type === "note_added" && msg.payload && currentRef.current && msg.payload.incident_id === currentRef.current.id) {
      setNotes((n) => [...n, msg.payload]);
    }
    if (msg.type === "incident_closed" && msg.payload) {
      setIncidents((prev) => {
        const next = prev.filter((i) => i.id !== msg.payload.id);
        setIndex((idx) => (next.length ? Math.min(idx, next.length - 1) : 0));
        return next;
      });
    }
    if (msg.type === "incident_repaged" && msg.payload) {
      const p = msg.payload;
      if (!matchesLoc(p)) return;
      setIncidents((prev) => {
        const exists = prev.some((i) => i.id === p.id);
        const next = exists ? prev.map((i) => (i.id === p.id ? p : i)) : [...prev, p];
        const pos = next.findIndex((i) => i.id === p.id);
        if (pos >= 0) setIndex(pos);
        return next;
      });
      pauseUntilRef.current = Date.now() + 30000;
      const cs = p.added_callsigns || [];
      setExtraByInc((m) => ({ ...m, [p.id]: { callsigns: cs, until: Date.now() + 30000 } }));
      if (armedRef.current) speak(`Extra alarmering. ${cs.join(", ")}. Voor ${p.title}.`);
    }
    if (msg.type === "incident_ack" && msg.payload) {
      const inc = msg.payload.incident;
      if (inc) setIncidents((prev) => prev.map((i) => (i.id === inc.id ? inc : i)));
    }
    if (msg.type === "incident_retracted" && msg.payload) {
      const inc = msg.payload.incident;
      if (msg.payload.scope === "all") {
        setIncidents((prev) => {
          const next = prev.filter((i) => i.id !== msg.payload.incident_id);
          setIndex((idx) => (next.length ? Math.min(idx, next.length - 1) : 0));
          return next;
        });
      } else if (inc) {
        setIncidents((prev) => prev.map((i) => (i.id === inc.id ? inc : i)));
      }
    }
  }, phase === "ready" ? token : null);

  useEffect(() => {
    if (phase !== "ready") return;
    monitorApi.get("/map-layer").then(({ data }) => setMonShared(data.enabled !== false ? data.drawings : null)).catch(() => {});
    monitorApi.get("/settings/color-legend").then(({ data }) => setMonLegend(data.legend || null)).catch(() => {});
    monitorApi.get("/settings/map").then(({ data }) => { setRepeatSecs(Number(data.monitor_repeat_seconds) || 0); setGlobalIdle(data.monitor_idle_mode || "time"); setFlActive(data.flightroute_active || ""); setFlLeft(data.flightroute_left_name || ""); setFlRight(data.flightroute_right_name || ""); setMonShowFl(!!data.monitor_show_flightroute); setFlDoc({ present: !!data.flight_doctor_present, until: data.flight_doctor_until || null }); }).catch(() => {});
    monitorApi.get("/tasks").then(({ data }) => setTasks(data || [])).catch(() => {});
    const _tt = setInterval(() => monitorApi.get("/tasks").then(({ data }) => setTasks(data || [])).catch(() => {}), 60000);
    return () => clearInterval(_tt);
  }, [phase]);

  const arm = () => {
    armedRef.current = true;
    setArmed(true);
    speak("Kazernemonitor gereed.");
    monitorApi.get("/incidents?status=open").then(({ data }) => {
      const list = (data || []).filter(matchesLoc);
      setIncidents(list);
      setIndex(0);
    }).catch(() => {});
  };

  const toggleTask = (task) => { monitorApi.patch(`/tasks/${task.id}`, { done: !task.done }).then(() => setTasks((ts) => ts.map((x) => x.id === task.id ? { ...x, done: !x.done } : x))).catch(() => {}); };

  useEffect(() => { if (armed && current) loadNotes(current.id); else if (!current) setNotes([]); }, [current?.id, armed]); // eslint-disable-line

  // Fallback-resync: vangt gemiste WS-events op zodat toewijzen/intrekken snel & accuraat blijft (max ~5s vertraging).
  useEffect(() => {
    if (phase !== "ready" || !armed) return;
    const t = setInterval(() => {
      monitorApi.get("/incidents?status=open").then(({ data }) => {
        const list = (data || []).filter(matchesLoc);
        setIncidents((prev) => {
          if (prev.map((i) => i.id).join(",") === list.map((i) => i.id).join(",")) return prev;
          setIndex((idx) => (list.length ? Math.min(idx, list.length - 1) : 0));
          return list;
        });
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [phase, armed, matchesLoc]);

  // Announce a newly-received melding once (PRIO + titel + locatie); never re-announce on rotation.
  useEffect(() => {
    if (!armed || !current) return;
    if (announcedRef.current.has(current.id)) return;
    announcedRef.current.add(current.id);
    announce(current);
  }, [current?.id, armed]); // eslint-disable-line

  // Repeat the announcement every X seconds until the melding is acknowledged (configurable in Beheer).
  useEffect(() => {
    if (!armed || !current || !repeatSecs) return;
    if ((current.acks || []).length > 0) return;
    const t = setInterval(() => { if (currentRef.current && (currentRef.current.acks || []).length === 0) announce(currentRef.current); }, repeatSecs * 1000);
    return () => clearInterval(t);
  }, [armed, current?.id, current?.acks?.length, repeatSecs]); // eslint-disable-line

  useEffect(() => {
    const t = setInterval(() => setWsStale(lastMsgAt > 0 && Date.now() - lastMsgAt > 35000), 5000);
    return () => clearInterval(t);
  }, [lastMsgAt]);

  // Groot-scherm (50"): schaal alle rem-maten mee met de schermbreedte.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.fontSize;
    root.style.fontSize = "clamp(16px, 1.25vw, 48px)";
    return () => { root.style.fontSize = prev; };
  }, []);

  // Installed PWA opens straight to this app (not the command-center login).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", "/manifest-kazerne.json");
  }, []);

  // Kiosk: ga naar volledig scherm bij de eerste aanraking/klik (browsers vereisen een gebruikersgebaar).
  useEffect(() => {
    const go = () => { const el = document.documentElement; if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {}); };
    window.addEventListener("click", go, { once: true });
    window.addEventListener("touchstart", go, { once: true });
    return () => { window.removeEventListener("click", go); window.removeEventListener("touchstart", go); };
  }, []);

  // Auto-carousel: wissel elke 12s tussen meerdere gelijktijdige incidenten (pauzeer bij een verse extra-alarmering)
  useEffect(() => {
    if (!armed || incidents.length <= 1) return;
    const t = setInterval(() => { if (Date.now() < pauseUntilRef.current) return; setIndex((i) => (i + 1) % incidents.length); }, 12000);
    return () => clearInterval(t);
  }, [armed, incidents.length]);

  const timeStr = now.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const docState = flightDoctorState(flDoc.present, flDoc.until, now);
  const dateStr = now.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (phase === "loading") {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500">{t("dev.loading")}</div>;
  }

  if (phase === "enroll") {
    return (
      <div data-testid="monitor-enroll" className="relative flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="absolute right-5 top-4"><LanguageSwitcher /></div>
        <form onSubmit={enroll} className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl shadow-slate-200/60">
          <div className="mb-1 flex items-center gap-2 text-lg font-bold"><Tv className="h-5 w-5 text-blue-600" /> {t("dev.monTitle")}</div>
          <p className="mb-4 text-sm text-slate-500">{t("dev.monEnrollHint")}</p>
          <div className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="h-4 w-4" /> {t("dev.codeLabel")}</div>
          <input data-testid="monitor-enroll-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Bijv. 7GX2QP" maxLength={8}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-3 text-center font-mono text-lg tracking-widest outline-none focus:border-blue-500" />
          <button data-testid="monitor-enroll-submit" type="submit" className="mt-3 w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500">{t("dev.monPair")}</button>
        </form>
      </div>
    );
  }

  if (!armed) {
    return (
      <div data-testid="monitor-arm-screen" className="relative flex h-screen flex-col items-center justify-center bg-slate-50 px-6 text-center text-slate-900">
        <div className="absolute right-5 top-4"><LanguageSwitcher /></div>
        <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70"><Tv className="h-12 w-12 text-blue-600" /></div>
        <h1 className="font-display text-3xl font-bold">Kazernemonitor</h1>
        <p className="mt-3 max-w-md text-sm text-slate-500">{t("dev.monArmHint")}</p>
        <button data-testid="monitor-arm-btn" onClick={arm}
          className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-8 py-4 text-lg font-bold text-white shadow-lg shadow-blue-600/25 transition-transform hover:scale-[1.03] active:scale-95">
          <Power className="h-5 w-5" /> {t("dev.monStart")}
        </button>
        <div className="mt-6 flex items-center gap-3">
          <SpeechSettings rate={speechRate} setRate={setSpeechRate} volume={speechVolume} setVolume={setSpeechVolume} speak={speak} />
          <button data-testid="monitor-unpair" onClick={unpair} className="text-xs text-slate-500 hover:text-slate-800">{t("dev.monUnpair")}</button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div data-testid="monitor-home" className="flex h-screen bg-slate-50 text-slate-900">
        {(() => {
          const refillItems = tasks.filter((t) => t.kind === "rapid_refill");
          const noteItems = tasks.filter((t) => t.kind === "note");
          if (refillItems.length === 0 && noteItems.length === 0) return null;
          return (
            <div data-testid="monitor-side-panel" className="flex w-[24rem] shrink-0 flex-col gap-4 overflow-y-auto p-5">
              {refillItems.length > 0 && (
                <div data-testid="monitor-refills" className="gold-widget overflow-hidden rounded-2xl">
                  <div className="flex items-center gap-2 bg-blue-600 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white"><HelicopterIcon className="h-5 w-5" /> Rapid Refuel</div>
                  <div className="space-y-2 p-3">
                    {refillItems.map((r) => {
                      const cd = fmtCountdown(r.date, r.start_time, now);
                      const mins = minsTo(r.date, r.start_time, now);
                      const soon = mins != null && mins > 0 && mins <= 15;
                      return (
                        <div key={r.id} data-testid={`monitor-refill-${r.id}`} className={`rounded-xl border px-3 py-2 ${soon ? "border-yellow-400 bg-yellow-50 animate-pulse" : "border-slate-200 bg-white"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate text-xl font-bold text-slate-900">{r.aircraft || r.title}</div>
                              <div className="truncate text-sm text-slate-500">{r.vehicle ? `Door ${r.vehicle}` : ""}{r.start_time ? ` · ${r.start_time}` : ""}</div>
                            </div>
                            {cd && <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold ${soon ? "bg-yellow-400 text-slate-900" : "bg-slate-900 text-white"}`}>{cd}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {noteItems.length > 0 && (
                <div data-testid="monitor-notes-board" className="gold-widget overflow-hidden rounded-2xl">
                  <div className="flex items-center gap-2 bg-yellow-400 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-900"><StickyNote className="h-5 w-5" /> Notities</div>
                  <div className="space-y-2 p-3">
                    {noteItems.map((n) => (
                      <div key={n.id} data-testid={`monitor-note-${n.id}`} className="rounded-xl border-l-4 border-yellow-400 bg-slate-50 px-3 py-2 text-lg font-medium text-slate-800">{n.title}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <div data-testid="monitor-main" className="relative flex min-w-0 flex-1 flex-col items-center justify-center px-6">
        {monLocName && (
          <div data-testid="monitor-location" className="absolute bottom-4 right-5 gold-widget inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm">
            <MapPin className="h-4 w-4 text-blue-600" /> {monLocName}
          </div>
        )}
        <div className="absolute right-5 top-4 flex items-center gap-3">
          <LanguageSwitcher />
          <span data-testid="monitor-online" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${wsStatus === "live" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : wsStatus === "degraded" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-500"}`}>
            <Radio className="h-3.5 w-3.5" /> {wsStatus === "live" ? "Verbonden" : wsStatus === "degraded" ? "Herverbinden…" : "Verbinden…"}
          </span>
          <SpeechSettings rate={speechRate} setRate={setSpeechRate} volume={speechVolume} setVolume={setSpeechVolume} speak={speak} />
          <button data-testid="monitor-unpair" onClick={unpair} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-100"><LogOut className="mr-1 inline h-3.5 w-3.5" />Ontkoppel</button>
        </div>
        <div className="mb-4 flex flex-wrap items-center justify-center gap-4">
        {monShowFl && flActive && (
          <div data-testid="monitor-flightroute" className="inline-flex items-center gap-2 rounded-2xl bg-yellow-400 px-8 py-3 text-3xl font-extrabold text-slate-900 shadow-lg shadow-yellow-500/30">
            <_PlaneTakeoff className="h-7 w-7" /> {flActive === "left" ? (flLeft || "Vliegroute links") : (flRight || "Vliegroute rechts")}
          </div>
        )}
          <div data-testid="monitor-flight-doctor" className={`inline-flex items-center gap-3 rounded-2xl px-8 py-3 text-3xl font-extrabold text-white shadow-lg ${docState.active ? "bg-emerald-600 shadow-emerald-600/30" : "bg-red-600 shadow-red-600/30"}`}>
            <Stethoscope className="h-7 w-7" /> Vlieger Arts
            <span data-testid="monitor-flight-doctor-state" className="rounded-xl bg-white/20 px-3 py-1 text-lg uppercase tracking-wide">{docState.label}</span>
          </div>
        </div>
        {(idleMode || globalIdle) === "tasks" ? (
          <MonitorTaskBoard tasks={tasks.filter((t) => (t.kind || "task") === "task")} now={now} dateStr={dateStr} timeStr={timeStr} onToggle={toggleTask} />
        ) : (
          <>
            <div data-testid="monitor-idle-clock" className="mt-2 font-clock font-extrabold leading-[0.85] text-slate-900" style={{ fontSize: "min(32rem, 50vh)" }}>{timeStr}</div>
            <div className="mt-3 text-xl capitalize text-slate-500">{dateStr}</div>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-600/20">
              <span className="h-2 w-2 rounded-full bg-white live-dot" /> Gereed — geen actieve melding
            </div>
          </>
        )}
        </div>
      </div>
    );
  }

  const P = PRIORITIES[current.priority] || PRIORITIES[4];
  const veh = vehOf(current);
  const extra = extraByInc[current.id];
  const showExtra = extra && Date.now() < extra.until && (extra.callsigns || []).length > 0;
  const acks = current.acks || [];
  return (
    <div data-testid="monitor-alarm" className="flex h-screen flex-col bg-slate-100 text-slate-900">
      <div className="flex shrink-0 items-center justify-between px-8 py-3 text-white shadow-md pager-flash" style={{ backgroundColor: P.hex }}>
        <div className="min-w-0">
          <div className="text-sm font-bold uppercase tracking-[0.2em] opacity-90">{P.short} · {P.label}</div>
          <h1 data-testid="monitor-title" className="truncate font-display text-4xl font-black leading-tight">{current.title}</h1>
          {current.subcategory && <div data-testid="monitor-subcategory" className="truncate text-xl font-black opacity-90">{current.subcategory}</div>}
        </div>
        <div className="shrink-0 text-right">
          <div className="mb-1 flex items-center justify-end">
            <span data-testid="monitor-online" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${wsStatus === "degraded" ? "bg-amber-500/30 text-amber-100" : "bg-white/20 text-white"}`}>
              <Radio className="h-3.5 w-3.5" /> {wsStatus === "live" ? "Verbonden" : wsStatus === "degraded" ? "Herverbinden…" : "Verbinden…"}
            </span>
          </div>
          <div className="font-mono text-sm opacity-90">{current.ref}</div>
          <div data-testid="monitor-alarm-clock" className="font-clock text-7xl font-extrabold leading-none">{timeStr}</div>
        </div>
      </div>

      {incidents.length > 1 && (
        <div data-testid="monitor-carousel-bar" className="flex shrink-0 items-center justify-center gap-4 border-b border-slate-200 bg-white py-1.5 text-sm font-semibold text-slate-700">
          <span data-testid="monitor-carousel-count">Melding {index + 1} van {incidents.length}</span>
          <div className="flex items-center gap-1.5">
            {incidents.map((inc, i) => (
              <span key={inc.id} className={`h-2.5 w-2.5 rounded-full transition-opacity ${i === index ? "" : "opacity-30"}`}
                style={{ backgroundColor: (PRIORITIES[inc.priority] || PRIORITIES[4]).hex }} />
            ))}
          </div>
          <span className="text-xs font-normal text-slate-400">wisselt automatisch</span>
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-3 gap-4 overflow-hidden p-4">
        <div className="col-span-1 flex min-h-0 flex-col gap-3 overflow-hidden">
          <div className="gold-widget shrink-0 rounded-2xl px-4 py-3" data-testid="monitor-location-card">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Locatie</div>
            <div className="flex items-center gap-2 text-2xl font-bold leading-tight text-slate-900"><MapPin className="h-6 w-6 shrink-0 text-blue-600" /> <span className="line-clamp-2">{locOf(current)}</span></div>
            {current.building?.function && <div data-testid="monitor-building-function" className="mt-1 flex items-center gap-2 text-lg font-semibold text-blue-700"><Building2 className="h-5 w-5" /> {current.building.function}</div>}
            <div className="mt-2 border-t border-slate-100 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Voor voertuig</div>
            <div data-testid="monitor-vehicle" className="flex items-center gap-2 text-2xl font-bold leading-tight text-slate-900"><Truck className="h-6 w-6 shrink-0 text-blue-600" /> <span className="line-clamp-2">{veh || "—"}</span></div>
          </div>
          {showExtra && (
            <div data-testid="monitor-extra" className="shrink-0 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm pager-flash">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700"><Plus className="h-3.5 w-3.5" /> Extra gealarmeerd</div>
              <div className="flex items-center gap-2 text-2xl font-bold text-amber-900"><Truck className="h-6 w-6" /> {extra.callsigns.join(", ")}</div>
            </div>
          )}
          {(acks.length > 0 || (current.vehicles || []).length > 0) && (
            <div data-testid="monitor-acks" className="gold-widget shrink-0 rounded-2xl px-4 py-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><CheckCheck className="h-3.5 w-3.5" /> Bevestigd door</div>
                {(current.vehicles || []).length > 0 && (
                  <span data-testid="monitor-ack-count" className={`rounded-full px-3 py-1 text-base font-bold tabular-nums ${acks.length >= (current.vehicles || []).length ? "bg-emerald-600 text-white" : "bg-yellow-400 text-slate-900"}`}>{acks.length}/{(current.vehicles || []).length} bevestigd</span>
                )}
              </div>
              {acks.length === 0 ? (
                <div className="text-sm text-slate-400">Nog geen bevestigingen ontvangen.</div>
              ) : (
                <div className="flex max-h-24 flex-wrap gap-2 overflow-hidden">
                  {acks.map((a, i) => (
                    <span key={`${a.id}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800">{a.name}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex min-h-[6rem] flex-1 flex-col gold-widget rounded-2xl px-4 py-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Kladblok</div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto" data-testid="monitor-notes">
              {notes.length === 0 ? (
                <div className="text-sm text-slate-400">Nog geen kladblokregels.</div>
              ) : notes.map((n) => (
                <div key={n.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                  <span className="mr-2 font-mono text-[11px] text-slate-500">{new Date(n.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="font-semibold text-slate-700">{n.author}:</span> <span className="text-slate-900">{n.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="gold-widget col-span-2 overflow-hidden rounded-2xl">
          <MonitorMap inc={current} shared={monShared} legend={monLegend} />
        </div>
      </div>
    </div>
  );
}
